import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  inject,
  LOCALE_ID,
  NgModule,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { CompaniesService } from '../../../../services/companies/companies.service';
import { finalize } from 'rxjs';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CompaniesResponseType,
  CompanyResponseType,
} from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { companiesMapper } from './view-all-companies-component.mapper';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';

@Component({
  selector: 'app-view-all-companies-component',
  standalone: true,
  imports: [FormsModule, Tables],
  templateUrl: './view-all-companies-component.html',
  styleUrl: './view-all-companies-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewAllCompaniesComponent implements OnInit {
  private router = inject(Router);

  private companyService = inject(CompaniesService);
  private localeId = inject(LOCALE_ID);
  isArabic = computed(() => this.localeId.startsWith('ar'));

  currentPage = 1;
  itemsPerPage = 25;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  filters: {
    companyTitle?: string;
    createdOnFrom?: string;
    createdOnTo?: string;

    orderBy?: 'TITLE' | 'CREATION_DATE' | 'LAST_MODIFICATION_DATE';
    orderDir?: 'ASC' | 'DESC';
  } = {
    companyTitle: '',
    createdOnFrom: '',
    createdOnTo: '',
    orderBy: 'TITLE',
    orderDir: 'ASC',
  };

  appliedFilters: typeof this.filters = { ...this.filters };

  companies: CompanyResponseType[] = [];
  allCompanies: CompanyResponseType[] = [];
  totalItems = 0;

  loading = signal<boolean>(false);
  errorGettingCompanies = signal<string | null>(null);

  showDeleteDialog = false;
  showActivationDialog = signal<{
    status: boolean;
    type: 'activation' | 'deactivation';
  }>({
    status: false,
    type: 'activation',
  });
  companyToDelete: CompanyResponseType | null = null;
  companyToTakeAction = signal<{
    actionType: 'activation' | 'deactivation';
    id: number;
  } | null>(null);
  showSuccessMessage = false;

  activeDropdownId = signal<number | null>(null);
  companyTitleResults: { id: number; name: string }[] = [];
  companyTitleLoading = false;
  showCompanyTitleDropdown = false;
  private companyTitleSearchTimeout: any;
  currentActions = signal<ActionInterface[]>([]);

  tableHeaderData = signal([
    { key: 'id', label: '#' },
    {
      key: 'companyName',
      label: $localize`:@@adminCompaniesTableHeaderName:Company Name`,
      width: '38%',
    },
    { key: 'createdOnBy', label: $localize`:@@adminCompaniesTableHeaderDateCreated:Created On / By`, width: '45%' },
    { key: 'actions', label: '', type: 'actions', width: '17%' },
  ]);
  rows = computed(() => companiesMapper(this.paginatedCompanies, this.isArabic()));

  ngOnInit() {
    this.loadCompanies();
  }

  getCompanyTitle(company: CompanyResponseType): string {
    return this.localeId === 'ar' ? company.titleAr || company.titleEn : company.titleEn;
  }

  onCompanyTitleChange(value: string) {
    this.filters.companyTitle = value;
    clearTimeout(this.companyTitleSearchTimeout);

    if (!value) {
      this.companyTitleResults = [];
      this.companyTitleLoading = false;
      this.showCompanyTitleDropdown = false;
      return;
    }

    this.showCompanyTitleDropdown = true;
    this.companyTitleLoading = true;

    this.companyTitleSearchTimeout = setTimeout(() => {
      this.searchCategoryTitles(value);
    }, 400);
  }

  onCompanyTitleFocus() {
    if (this.filters.companyTitle) {
      this.showCompanyTitleDropdown = true;
      if (!this.companyTitleResults.length) {
        this.companyTitleLoading = true;
        this.searchCategoryTitles(this.filters.companyTitle);
      }
    }
  }

  selectCompanyTitle(item: { id: number; name: string }) {
    this.filters.companyTitle = item.name;
    this.showCompanyTitleDropdown = false;
  }

  private searchCategoryTitles(term: string) {
    this.companyService.getMFGCompanies().subscribe({
      next: (res: any) => {
        this.companyTitleResults =
          res.list?.map((c: any) => ({
            id: c.id,
            name: c.titleEn,
          })) ?? [];
        this.companyTitleLoading = false;
      },
      error: () => {
        this.companyTitleResults = [];
        this.companyTitleLoading = false;
      },
    });
  }

  loadCompanies() {
    this.loading.set(true);
    console.log('orderDir:', this.appliedFilters.orderDir);

    const apiParams = {
      // companyTitle: this.appliedFilters.companyTitle || undefined,
      // pageSize: 25,
      // pageNum: 0,
      // pageOffset: 0,
      orderBy: this.appliedFilters.orderBy,
      orderDir: this.appliedFilters.orderDir,
    };

    this.companyService
      .getMFGCompanies(apiParams)
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe({
        next: (res: CompaniesResponseType) => {
          // let list = (res.list || []).map((c: CompanyResponseType) => ({
          //   id: c.id,
          //   name: c.titleEn,
          //   dateCreated: c.createdOn || '',
          //   lastUpdated: c.createdOn || '',
          // }));
          // if (this.appliedFilters.createdOnFrom) {
          //   list = list.filter((c: any) => {
          //     if (!c.dateCreated) return false;
          //     const createdDate = new Date(c.dateCreated);
          //     const filterDate = new Date(this.appliedFilters.createdOnFrom!);
          //     return createdDate >= filterDate;
          //   });
          // }

          // if (this.appliedFilters.createdOnTo) {
          //   list = list.filter((c: any) => {
          //     if (!c.dateCreated) return false;
          //     const createdDate = new Date(c.dateCreated);
          //     const filterDate = new Date(this.appliedFilters.createdOnTo!);
          //     filterDate.setHours(23, 59, 59, 999);
          //     return createdDate <= filterDate;
          //   });
          // }

          this.allCompanies = res.list;
          this.totalItems = res.total;
          this.companies = res.list;
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.errorGettingCompanies.set('Error fetching categories. Please try again later.');
        },
      });
  }

  toggleSortOrder() {
    this.filters.orderDir = this.filters.orderDir == 'DESC' ? 'ASC' : 'DESC';
    // this.loadCompanies();
    this.applyFilters();
  }

  get sortedCompanies(): CompanyResponseType[] {
    const companiesToSort = this.companies;

    if (!this.sortColumn) {
      return companiesToSort;
    }

    return [...companiesToSort].sort((a, b) => {
      const aValue = (a as any)[this.sortColumn];
      const bValue = (b as any)[this.sortColumn];

      let comparison = 0;
      if (aValue != null && bValue != null) {
        if (aValue > bValue) {
          comparison = 1;
        } else if (aValue < bValue) {
          comparison = -1;
        }
      }

      return this.sortDirection == 'asc' ? comparison : -comparison;
    });
  }

  get paginatedCompanies(): CompanyResponseType[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.sortedCompanies.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get startItem(): number {
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endItem(): number {
    const end = this.currentPage * this.itemsPerPage;
    return end > this.totalItems ? this.totalItems : end;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 100);
    for (let i = 1; i <= maxPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  sort(column: string) {
    if (this.sortColumn == column) {
      this.sortDirection = this.sortDirection == 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
  }

  openDeleteDialog(company: CompanyResponseType) {
    this.companyToDelete = company;
    this.showDeleteDialog = true;
  }

  closeDeleteDialog() {
    this.showDeleteDialog = false;
    this.companyToDelete = null;
  }

  openActivationDialog(companyId: number, type: 'activation' | 'deactivation') {
    this.companyToTakeAction.set({
      actionType: type,
      id: companyId,
    });
    this.showActivationDialog.set({
      type: type,
      status: true,
    });
  }

  closeActivationDialog() {
    this.showActivationDialog.set({
      type: this.showActivationDialog().type,
      status: false,
    });
  }

  deactivateCompanyFn() {
    const companyId = this.companyToTakeAction()!.id;
    this.companyService.deactivateCompany(companyId).subscribe({
      next: () => {
        const updateCompany = this.companies.find((c) => c.id === companyId);
        if (updateCompany) {
          updateCompany.isActive = false;
        }
        this.closeActivationDialog();
      },
      error: () => {},
    });
  }

  activateCompany() {
    const companyId = this.companyToTakeAction()!.id;

    this.companyService.activateCompany(companyId).subscribe({
      next: () => {
        const updatedCompany = this.companies.find((c) => c.id === companyId);
        if (updatedCompany) {
          updatedCompany.isActive = true;
        }
        this.closeActivationDialog();
      },
      error: () => {},
    });
  }

  deleteCompany() {
    if (this.companyToDelete) {
      const deletedId = this.companyToDelete.id;
      this.closeDeleteDialog();
      this.companyService.deleteCompany(deletedId).subscribe({
        next: () => {
          this.companies = this.companies.filter((c) => c.id !== deletedId);
          this.allCompanies = this.allCompanies.filter((c) => c.id !== deletedId);
          this.totalItems--;
          if (this.paginatedCompanies.length == 0 && this.currentPage > 1) {
            this.currentPage--;
          }
          this.showSuccessMessage = true;
          setTimeout(() => {
            this.showSuccessMessage = false;
          }, 3000);
          this.loadCompanies();
        },
        error: (error) => {
          console.error('Error deleting category:', error);
        },
      });
    }
  }

  applyFilters() {
    this.appliedFilters = { ...this.filters };
    this.currentPage = 1;
    this.loadCompanies();
  }

  resetFilters() {
    this.filters = {
      companyTitle: '',
      createdOnFrom: '',
      createdOnTo: '',

      orderBy: 'TITLE',
      orderDir: 'ASC',
    };
    this.appliedFilters = { ...this.filters };
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.currentPage = 1;
    this.loadCompanies();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.activeDropdownId.set(null);
    this.showCompanyTitleDropdown = false;
  }

  toggleDropdown(id: any) {
    console.log('id: ', id);
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const company = this.paginatedCompanies?.find((p) => {
        console.log('p: ', p);

        return p.id - 1 === +id;
      });
      this.getActions(company!);
    }
  }

  getActions(company: CompanyResponseType) {
    console.log('company: ', company);
    this.currentActions.set([
      { id: 1, titleEn: 'View Company Details', titleAr: 'عرض تفاصيل شركة' },
      { id: 2, titleEn: 'Edit Company', titleAr: 'تعديل شركة' },
      {
        id: 3,
        titleEn: company?.isActive ? 'Deactivate Company' : 'Activate Company',
        titleAr: company?.isActive ? 'إلغاء تفعيل الشركة' : 'تفعيل الشركة',
      },
      { id: 4, titleEn: 'Delete Company', titleAr: 'حذف الشركة' },
    ]);
  }

  handleAction(e: { actionId: number; entity: CompanyResponseType }) {
    const { actionId, entity: company } = e;
    console.log('e: ', e);
    if (actionId === 1) {
      this.onViewCompany(company.id);
    } else if (actionId === 2) {
      this.onEditCompany(company.id);
    } else if (actionId === 3) {
      this.openActivationDialog(+company.id, 'activation');
    } else if (actionId === 5) {
      this.openActivationDialog(+company.id, 'deactivation');
    } else if (actionId === 4) {
      this.openDeleteDialog(company);
    }
    this.activeDropdownId.set(null);
  }

  onViewCompany(companyId: number) {
    this.router.navigate(['/admin/companies/view-company', companyId]);
  }

  onEditCompany(companyId: number) {
    this.router.navigate(['/admin/companies/edit-company', companyId]);
  }
}
